import dayjs from "dayjs";
import { MouseEvent } from "react";
import { useTranslation } from "react-i18next";
import {
  ActorAvailability,
  PlanningComparison as PlanningComparisonModel,
  PlanningSuggestion,
  TimeInterval,
} from "../../services/scheduling/AppointmentPlanning.types";
import styles from "./AppointmentPlanner.module.css";

interface PlanningComparisonProps {
  comparison?: PlanningComparisonModel;
  selected?: PlanningSuggestion;
  onSelect: (suggestion: PlanningSuggestion) => void;
  onSelectStart: (start: string) => void;
}

function intervalLabel(start: string, end: string): string {
  const startValue = dayjs(start);
  const endValue = dayjs(end);
  return `${startValue.format("ddd D MMM · HH:mm")} – ${endValue.format("HH:mm")}`;
}

function percent(value: string, range: TimeInterval): number {
  const total = dayjs(range.end).valueOf() - dayjs(range.start).valueOf();
  if (total <= 0) {
    return 0;
  }
  return Math.max(
    0,
    Math.min(
      100,
      ((dayjs(value).valueOf() - dayjs(range.start).valueOf()) / total) * 100,
    ),
  );
}

function width(start: string, end: string, range: TimeInterval): number {
  return Math.max(0.35, percent(end, range) - percent(start, range));
}

function TimelineRow({
  availability,
  range,
  selected,
  onSelectStart,
}: {
  availability: ActorAvailability;
  range: TimeInterval;
  selected?: PlanningSuggestion;
  onSelectStart: (start: string) => void;
}) {
  const { t } = useTranslation();

  const selectFromPointer = (event: MouseEvent<HTMLDivElement>) => {
    const bounds = event.currentTarget.getBoundingClientRect();
    const ratio = Math.max(0, Math.min(1, (event.clientX - bounds.left) / bounds.width));
    const rangeStart = dayjs(range.start).valueOf();
    const rangeDuration = dayjs(range.end).valueOf() - rangeStart;
    const raw = rangeStart + rangeDuration * ratio;
    const step = 15 * 60_000;
    const snapped = Math.round(raw / step) * step;
    onSelectStart(new Date(snapped).toISOString());
  };

  return (
    <div className={styles.timelineRow}>
      <div className={styles.timelineActor}>
        <strong>{availability.actor.display}</strong>
        <small>{t(`calendar.actorTypes.${availability.actor.type}`)}</small>
      </div>
      <div
        className={styles.timelineTrack}
        role="button"
        tabIndex={0}
        aria-label={t("planner.comparison.chooseFor", {
          name: availability.actor.display,
        })}
        onClick={selectFromPointer}
      >
        {availability.slots.map((slot) => (
          <span
            key={`slot-${slot.id}`}
            className={slot.status === "free" ? styles.slotFree : styles.slotBusy}
            style={{
              left: `${percent(slot.start, range)}%`,
              width: `${width(slot.start, slot.end, range)}%`,
            }}
            title={`${slot.scheduleLabel} · ${slot.status} · ${intervalLabel(slot.start, slot.end)}`}
          />
        ))}
        {availability.appointments.map((appointment) => (
          <span
            key={`appointment-${appointment.id}`}
            className={styles.appointmentBlock}
            style={{
              left: `${percent(appointment.start, range)}%`,
              width: `${width(appointment.start, appointment.end, range)}%`,
            }}
            title={`${appointment.title} · ${intervalLabel(appointment.start, appointment.end)}`}
          >
            {appointment.title}
          </span>
        ))}
        {selected && (
          <span
            className={styles.selectionBlock}
            style={{
              left: `${percent(selected.start, range)}%`,
              width: `${width(selected.start, selected.end, range)}%`,
            }}
            aria-hidden="true"
          />
        )}
      </div>
    </div>
  );
}

export default function PlanningComparison({
  comparison,
  selected,
  onSelect,
  onSelectStart,
}: PlanningComparisonProps) {
  const { t } = useTranslation();

  if (!comparison) {
    return (
      <div className={styles.comparisonEmpty}>
        {t("planner.comparison.notLoaded")}
      </div>
    );
  }

  const selectedConflictCount = selected
    ? Object.values(selected.conflictsByActor).reduce(
        (count, conflicts) => count + conflicts.length,
        0,
      )
    : 0;

  return (
    <section className={styles.comparison} aria-label={t("planner.comparison.title")}>
      <header className={styles.sectionHeader}>
        <div>
          <span>{t("planner.comparison.eyebrow")}</span>
          <h2>{t("planner.comparison.title")}</h2>
          <p className={styles.comparisonHint}>{t("planner.comparison.hint")}</p>
        </div>
        <strong>
          {t("planner.comparison.suggestionCount", {
            count: comparison.suggestions.length,
          })}
        </strong>
      </header>

      <div className={styles.timelineLegend}>
        <span><i className={styles.legendFree} />{t("planner.comparison.slotFree")}</span>
        <span><i className={styles.legendBusy} />{t("planner.comparison.slotBusy")}</span>
        <span><i className={styles.legendAppointment} />{t("planner.comparison.appointment")}</span>
        <span><i className={styles.legendSelection} />{t("planner.comparison.selection")}</span>
      </div>

      <div className={styles.timelineScale}>
        <span>{dayjs(comparison.range.start).format("ddd D MMM · HH:mm")}</span>
        <span>{dayjs(comparison.range.end).format("ddd D MMM · HH:mm")}</span>
      </div>

      <div className={styles.timelineTable}>
        {comparison.actors.map((availability) => (
          <TimelineRow
            key={availability.actor.reference}
            availability={availability}
            range={comparison.range}
            selected={selected}
            onSelectStart={onSelectStart}
          />
        ))}
      </div>

      {selected && (
        <div className={selectedConflictCount > 0 ? styles.selectionWarning : styles.selectionSummary}>
          <strong>{intervalLabel(selected.start, selected.end)}</strong>
          <span>
            {selectedConflictCount > 0
              ? t("planner.comparison.conflicts", { count: selectedConflictCount })
              : t("planner.comparison.noKnownConflict")}
          </span>
        </div>
      )}

      <div className={styles.suggestions}>
        <h3>{t("planner.comparison.common")}</h3>
        <p>{t("planner.comparison.commonHint")}</p>
        {comparison.suggestions.length === 0 ? (
          <p>{t("planner.comparison.noCommon")}</p>
        ) : (
          <div className={styles.suggestionGrid}>
            {comparison.suggestions.slice(0, 24).map((suggestion) => {
              const active =
                selected?.start === suggestion.start &&
                selected?.end === suggestion.end;
              return (
                <button
                  type="button"
                  key={`${suggestion.start}-${suggestion.end}`}
                  className={active ? styles.suggestionActive : styles.suggestion}
                  onClick={() => onSelect(suggestion)}
                >
                  <strong>{dayjs(suggestion.start).format("HH:mm")}</strong>
                  <span>{dayjs(suggestion.start).format("ddd D MMM")}</span>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
}
